// ServicoModal.js - Modal para criar/editar serviço (reutilizável)
// ✅ Estilo idêntico aos modais de Clientes (cliente-modal-*)
// ✅ CORRIGIDO: fetchConfigEmpresa agora busca o IVA da empresa corretamente
// ✅ CORRIGIDO: URL sem 'api/' duplicado (o axios já tem baseURL '/api')
// ✅ CORRIGIDO: envia empresaId como query param
// ✅ CORRIGIDO: usa createPortal para aparecer SEMPRE por cima de qualquer modal
// ✅ CORRIGIDO: selects envolvidos em .servico-select-wrapper para seta única
// ✅ CORRIGIDO: typo 'ivavalue' → 'ivaValue' no handleEditSubmit
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Icon } from './SharedComponents';
import './ServicoModal.css';

const API_BASE_URL = '/api';

// ==============================================
// CONSTANTES
// ==============================================
const TIPOS_PRECO = [
    { value: 'Fixo', label: 'Preço Fixo' },
    { value: 'Hora', label: 'Por Hora' },
    { value: 'Mes', label: 'Por Mês' },
    { value: 'Projeto', label: 'Por Projeto' }
];

const CONFIG_PADRAO = {
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.'
    },
    iva: null
};

// ==============================================
// COMPONENTE PRINCIPAL
// ==============================================
export default function ServicoModal({
    onClose,
    onServicoCriado,
    onServicoEditado,
    servicoEdit,
    empresaId,
    isEdit = false
}) {
    // ==============================================
    // ESTADOS
    // ==============================================
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [categorias, setCategorias] = useState([]);
    const [configEmpresa, setConfigEmpresa] = useState(CONFIG_PADRAO);
    const [activeTab, setActiveTab] = useState('geral');

    // ==============================================
    // FORM DATA
    // ==============================================
    const [formData, setFormData] = useState({
        nome: '',
        descricao: '',
        codigo: '',
        precoUnitario: '',
        precoVenda: '',
        tipoPreco: 'Fixo',
        duracaoEstimada: '',
        categoriaId: '',
        ativo: true,
        impostos: {
            iva: null,
            ispc: 0,
            usarIvaEmpresa: true
        }
    });

    // ==============================================
    // API INSTANCE
    // ==============================================
    const api = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
        }
    });

    // ==============================================
    // FORMATAR MOEDA
    // ==============================================
    const formatarMoeda = (valor) => {
        const moeda = configEmpresa?.moeda || CONFIG_PADRAO.moeda;
        const { simbolo = 'MT', casasDecimais = 2, separadorDecimal = ',', separadorMilhar = '.' } = moeda;

        if (valor === undefined || valor === null || isNaN(valor)) {
            return `0${separadorDecimal}${'0'.repeat(casasDecimais)} ${simbolo}`;
        }

        let numero = Number(valor);
        if (isNaN(numero)) {
            return `0${separadorDecimal}${'0'.repeat(casasDecimais)} ${simbolo}`;
        }

        numero = numero.toFixed(casasDecimais);
        const partes = numero.split('.');
        let inteiros = partes[0];
        const decimais = partes[1] || '0'.repeat(casasDecimais);

        inteiros = inteiros.replace(/\B(?=(\d{3})+(?!\d))/g, separadorMilhar);

        return `${inteiros}${separadorDecimal}${decimais} ${simbolo}`;
    };

    // ==============================================
    // AUXILIARES
    // ==============================================
    const showMessage = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    };

    // ==============================================
    // OBTER EMPRESA ID (prop ou localStorage)
    // ==============================================
    const obterEmpresaId = () => {
        if (empresaId) return empresaId;
        try {
            const user = JSON.parse(localStorage.getItem('usuario') || '{}');
            return user.empresaId || user.empresa?._id || null;
        } catch (e) {
            console.error('❌ Erro ao obter empresaId:', e);
            return null;
        }
    };

    // ==============================================
    // CALCULAR PREÇO COM IVA
    // ==============================================
    const calcularPrecoComIVA = (precoSemIVA, iva) => {
        if (!precoSemIVA || precoSemIVA <= 0) return null;
        if (iva === null || iva === undefined || iva === 0) return precoSemIVA;
        const taxaIVA = Number(iva) / 100;
        return precoSemIVA * (1 + taxaIVA);
    };

    // ==============================================
    // CALCULAR PREÇO SEM IVA (INVERSO)
    // ==============================================
    const calcularPrecoSemIVA = (precoComIVA, iva) => {
        if (!precoComIVA || precoComIVA <= 0) return null;
        if (iva === null || iva === undefined || iva === 0) return precoComIVA;
        const taxaIVA = Number(iva) / 100;
        return precoComIVA / (1 + taxaIVA);
    };

    // ==============================================
    // IVA EFETIVO
    // ==============================================
    const getIvaEfetivo = (impostos) => {
        if (!impostos) return null;

        if (impostos.usarIvaEmpresa) {
            return configEmpresa?.iva !== null && configEmpresa?.iva !== undefined
                ? Number(configEmpresa.iva)
                : null;
        }

        return impostos.iva !== null && impostos.iva !== undefined
            ? Number(impostos.iva)
            : null;
    };

    // ==============================================
    // SANITIZAR PREÇO
    // ==============================================
    const sanitizarPreco = (valor) => {
        if (!valor) return '';
        let val = valor.replace(/[^0-9.]/g, '');
        const partes = val.split('.');
        if (partes.length > 2) {
            val = partes[0] + '.' + partes.slice(1).join('');
        }
        if (val.startsWith('.')) {
            val = '0' + val;
        }
        return val;
    };

    // ==============================================
    // BUSCAR CATEGORIAS
    // ==============================================
    const fetchCategorias = async () => {
        try {
            const response = await api.get('categorias');

            if (response.data && response.data.success) {
                const categoriasData = response.data.data || [];
                setCategorias(categoriasData);
                console.log(`✅ ${categoriasData.length} categorias carregadas`);
            } else if (Array.isArray(response.data)) {
                setCategorias(response.data);
                console.log(`✅ ${response.data.length} categorias carregadas`);
            } else {
                setCategorias([]);
            }
        } catch (err) {
            console.error('❌ Erro ao carregar categorias:', err);
            setCategorias([]);
        }
    };

    // ==============================================
    // 🔥 BUSCAR CONFIG EMPRESA (IVA) — CORRIGIDO
    // ==============================================
    const fetchConfigEmpresa = async () => {
        try {
            const empresaIdParaBuscar = obterEmpresaId();

            console.log('📡 Buscando config da empresa. empresaId:', empresaIdParaBuscar);

            if (!empresaIdParaBuscar) {
                console.warn('⚠️ empresaId não encontrado, usando config padrão');
                setConfigEmpresa(CONFIG_PADRAO);
                return;
            }

            // ✅ URL SEM 'api/' duplicado — o axios já tem baseURL '/api'
            // ✅ EmpresaId passado como query param
            const url = `servicos?limit=1&empresaId=${empresaIdParaBuscar}`;

            console.log('📡 GET', url);
            const response = await api.get(url);

            console.log('📥 Resposta completa:', response.data);

            if (response.data && response.data.configuracao) {
                const config = response.data.configuracao;
                const empresaData = response.data.empresa;

                console.log('✅ Config carregada:');
                console.log('   - IVA:', config.iva);
                console.log('   - Moeda:', config.moeda);
                console.log('   - Empresa:', empresaData?.nome || 'N/A');

                setConfigEmpresa(prev => ({
                    ...prev,
                    iva: (config.iva !== null && config.iva !== undefined) ? Number(config.iva) : null,
                    moeda: config.moeda || CONFIG_PADRAO.moeda,
                    regimeIVA: config.regimeIVA || 'Normal',
                    nome: empresaData?.nome || '',
                    nuit: empresaData?.nuit || ''
                }));
                return;
            }

            // Fallback via /produtos
            try {
                const urlProd = `produtos?limit=1&empresaId=${empresaIdParaBuscar}`;
                console.log('📡 Fallback GET', urlProd);
                const response2 = await api.get(urlProd);

                if (response2.data && response2.data.configuracao) {
                    const config = response2.data.configuracao;
                    console.log('✅ Config carregada via /produtos:', config);
                    setConfigEmpresa(prev => ({
                        ...prev,
                        iva: (config.iva !== null && config.iva !== undefined) ? Number(config.iva) : null,
                        moeda: config.moeda || CONFIG_PADRAO.moeda,
                        regimeIVA: config.regimeIVA || 'Normal'
                    }));
                    return;
                }
            } catch (err) {
                console.warn('⚠️ Fallback via /produtos falhou:', err.message);
            }

            console.warn('⚠️ Nenhuma config encontrada, usando padrão');
            setConfigEmpresa(CONFIG_PADRAO);

        } catch (error) {
            console.error('❌ Erro ao buscar config da empresa:', error);
            setConfigEmpresa(CONFIG_PADRAO);
        }
    };

    // ==============================================
    // CARREGAR DADOS INICIAIS — executa apenas 1 vez
    // ==============================================
    useEffect(() => {
        console.log('🚀 ServicoModal montado');
        fetchCategorias();
        fetchConfigEmpresa();
        
    }, []);

    // ==============================================
    // CARREGAR DADOS PARA EDIÇÃO
    // ==============================================
    useEffect(() => {
        if (isEdit && servicoEdit) {
            console.log('📝 Carregando serviço para edição:', servicoEdit.nome);

            const impostos = servicoEdit.impostos || {};

            let ivaOriginal = null;
            if (impostos.iva !== undefined && impostos.iva !== null) {
                ivaOriginal = Number(impostos.iva);
            }

            let usarIvaEmpresa = true;
            if (impostos.usarIvaEmpresa !== undefined) {
                usarIvaEmpresa = impostos.usarIvaEmpresa;
            } else {
                usarIvaEmpresa = ivaOriginal === null;
            }

            const ivaEfetivo = getIvaEfetivo(servicoEdit.impostos);
            const precoVenda = parseFloat(servicoEdit.precoVenda) || 0;
            let precoUnitario = precoVenda;

            if (ivaEfetivo !== null && ivaEfetivo !== undefined && precoVenda > 0) {
                const precoSemIva = calcularPrecoSemIVA(precoVenda, ivaEfetivo);
                if (precoSemIva !== null) {
                    precoUnitario = precoSemIva;
                }
            }

            setFormData({
                nome: servicoEdit.nome || '',
                descricao: servicoEdit.descricao || '',
                codigo: servicoEdit.codigo || '',
                precoUnitario: precoUnitario > 0 ? Math.round(precoUnitario * 100) / 100 : '',
                precoVenda: servicoEdit.precoVenda || '',
                tipoPreco: servicoEdit.tipoPreco || 'Fixo',
                duracaoEstimada: servicoEdit.duracaoEstimada || '',
                categoriaId: servicoEdit.categoriaId?._id || servicoEdit.categoriaId || '',
                ativo: servicoEdit.ativo !== undefined ? servicoEdit.ativo : true,
                impostos: {
                    iva: ivaOriginal,
                    ispc: impostos.ispc || 0,
                    usarIvaEmpresa: usarIvaEmpresa
                }
            });
        }
    }, [isEdit, servicoEdit]);

    // ==============================================
    // EFECTO PARA CALCULAR PREÇO DE VENDA
    // ==============================================
    const isUpdatingPrice = useRef(false);

    useEffect(() => {
        if (isUpdatingPrice.current) return;

        const precoUnitario = parseFloat(formData.precoUnitario) || 0;
        const ivaEfetivo = getIvaEfetivo(formData.impostos);

        if (precoUnitario > 0 && ivaEfetivo !== null && ivaEfetivo !== undefined) {
            const precoComIva = calcularPrecoComIVA(precoUnitario, ivaEfetivo);

            if (precoComIva !== null) {
                isUpdatingPrice.current = true;
                setFormData(prev => ({
                    ...prev,
                    precoVenda: Math.round(precoComIva * 100) / 100
                }));
                setTimeout(() => {
                    isUpdatingPrice.current = false;
                }, 100);
            }
        } else if (precoUnitario > 0 && (ivaEfetivo === null || ivaEfetivo === undefined)) {
            isUpdatingPrice.current = true;
            setFormData(prev => ({
                ...prev,
                precoVenda: precoUnitario
            }));
            setTimeout(() => {
                isUpdatingPrice.current = false;
            }, 100);
        }
    }, [formData.precoUnitario, formData.impostos.iva, formData.impostos.usarIvaEmpresa, configEmpresa?.iva]);

    // ==============================================
    // HANDLE CHANGE
    // ==============================================
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.startsWith('impostos.')) {
            const field = name.split('.')[1];

            if (field === 'iva') {
                const val = value === '' ? null : value;
                setFormData(prev => ({
                    ...prev,
                    impostos: { ...prev.impostos, [field]: val }
                }));
            } else if (field === 'usarIvaEmpresa') {
                setFormData(prev => ({
                    ...prev,
                    impostos: {
                        ...prev.impostos,
                        [field]: checked,
                        iva: checked ? null : prev.impostos.iva
                    }
                }));
            } else {
                const val = type === 'checkbox' ? checked : value;
                setFormData(prev => ({
                    ...prev,
                    impostos: { ...prev.impostos, [field]: val }
                }));
            }
        } else if (name === 'precoUnitario') {
            const val = sanitizarPreco(value);
            setFormData(prev => ({
                ...prev,
                [name]: val
            }));
        } else if (name === 'precoVenda') {
            const val = sanitizarPreco(value);
            setFormData(prev => ({
                ...prev,
                [name]: val
            }));
        } else {
            const val = type === 'checkbox' ? checked : value;
            setFormData(prev => ({ ...prev, [name]: val }));
        }
    };

    // ==============================================
    // RESET FORM
    // ==============================================
    const resetForm = () => {
        setFormData({
            nome: '',
            descricao: '',
            codigo: '',
            precoUnitario: '',
            precoVenda: '',
            tipoPreco: 'Fixo',
            duracaoEstimada: '',
            categoriaId: '',
            ativo: true,
            impostos: {
                iva: null,
                ispc: 0,
                usarIvaEmpresa: true
            }
        });
        setMessage({ text: '', type: '' });
        setActiveTab('geral');
    };

    // ==============================================
    // SUBMIT - CRIAR
    // ==============================================
    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ text: '', type: '' });

        if (!formData.nome.trim()) {
            setMessage({ text: 'O nome do serviço é obrigatório', type: 'danger' });
            setLoading(false);
            return;
        }

        if (!formData.precoVenda || parseFloat(formData.precoVenda) <= 0) {
            setMessage({ text: 'Preço de venda inválido', type: 'danger' });
            setLoading(false);
            return;
        }

        let empresaParaEnviar = empresaId;
        if (!empresaParaEnviar) {
            try {
                const user = JSON.parse(localStorage.getItem('usuario') || '{}');
                empresaParaEnviar = user.empresaId || user.empresa?._id || null;
            } catch (e) {
                console.error('❌ Erro ao obter empresaId:', e);
            }
        }

        if (!empresaParaEnviar) {
            setMessage({
                text: 'Utilizador sem empresa associada. Contacte o administrador.',
                type: 'danger'
            });
            setLoading(false);
            return;
        }

        let ivaEnviar = null;

        if (formData.impostos.usarIvaEmpresa) {
            ivaEnviar = null;
        } else {
            const ivaValue = formData.impostos.iva;
            if (ivaValue !== null && ivaValue !== undefined && ivaValue !== '') {
                ivaEnviar = Number(ivaValue);
            } else {
                ivaEnviar = null;
            }
        }

        const dadosParaEnviar = {
            nome: formData.nome.trim(),
            descricao: formData.descricao?.trim() || '',
            codigo: formData.codigo?.trim() || '',
            precoVenda: parseFloat(formData.precoVenda) || 0,
            tipoPreco: formData.tipoPreco || 'Fixo',
            duracaoEstimada: formData.duracaoEstimada ? parseFloat(formData.duracaoEstimada) : null,
            categoriaId: formData.categoriaId || null,
            empresaId: empresaParaEnviar,
            ativo: formData.ativo !== undefined ? formData.ativo : true,
            impostos: {
                iva: ivaEnviar,
                ispc: parseFloat(formData.impostos.ispc) || 0,
                usarIvaEmpresa: formData.impostos.usarIvaEmpresa
            }
        };

        try {
            console.log('📤 Criando serviço:', dadosParaEnviar);
            const response = await api.post('servicos', dadosParaEnviar);

            if (response.data) {
                showMessage('Serviço criado com sucesso!', 'success');

                if (onServicoCriado) {
                    onServicoCriado(response.data.data || response.data);
                }

                setTimeout(() => {
                    resetForm();
                    onClose();
                }, 1000);
            }
        } catch (err) {
            console.error('❌ Erro ao criar serviço:', err);
            setMessage({
                text: err.response?.data?.erro || err.response?.data?.mensagem || 'Erro ao criar serviço',
                type: 'danger'
            });
        } finally {
            setLoading(false);
        }
    };

    // ==============================================
    // SUBMIT - EDIÇÃO
    // ==============================================
    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ text: '', type: '' });

        if (!formData.nome.trim()) {
            setMessage({ text: 'O nome do serviço é obrigatório', type: 'danger' });
            setLoading(false);
            return;
        }

        if (!formData.precoVenda || parseFloat(formData.precoVenda) <= 0) {
            setMessage({ text: 'Preço de venda inválido', type: 'danger' });
            setLoading(false);
            return;
        }

        try {
            let ivaEnviar = null;

            if (formData.impostos.usarIvaEmpresa) {
                ivaEnviar = null;
            } else {
                const ivaValue = formData.impostos.iva;
                if (ivaValue !== null && ivaValue !== undefined && ivaValue !== '') {
                    ivaEnviar = Number(ivaValue);
                } else {
                    ivaEnviar = null;
                }
            }

            const dadosParaEnviar = {
                nome: formData.nome.trim(),
                descricao: formData.descricao?.trim() || '',
                codigo: formData.codigo?.trim() || '',
                precoVenda: parseFloat(formData.precoVenda) || 0,
                tipoPreco: formData.tipoPreco || 'Fixo',
                duracaoEstimada: formData.duracaoEstimada ? parseFloat(formData.duracaoEstimada) : null,
                categoriaId: formData.categoriaId || null,
                ativo: formData.ativo !== undefined ? formData.ativo : true,
                impostos: {
                    iva: ivaEnviar,
                    ispc: parseFloat(formData.impostos.ispc) || 0,
                    usarIvaEmpresa: formData.impostos.usarIvaEmpresa
                }
            };

            console.log('📤 Atualizando serviço:', dadosParaEnviar);
            const response = await api.put(`servicos/${servicoEdit._id}`, dadosParaEnviar);

            if (response.data) {
                showMessage('Serviço atualizado com sucesso!', 'success');

                if (onServicoEditado) {
                    onServicoEditado(response.data.data || response.data);
                }

                setTimeout(() => {
                    resetForm();
                    onClose();
                }, 1000);
            }
        } catch (err) {
            console.error('❌ Erro ao atualizar serviço:', err);
            setMessage({
                text: err.response?.data?.erro || err.response?.data?.mensagem || 'Erro ao atualizar serviço',
                type: 'danger'
            });
        } finally {
            setLoading(false);
        }
    };

    // ==============================================
    // RENDER
    // ==============================================
    const isEditMode = isEdit && servicoEdit;

    const precoUnitarioAtual = parseFloat(formData.precoUnitario) || 0;
    const ivaEfetivo = getIvaEfetivo(formData.impostos);
    const precoVendaCalculado = calcularPrecoComIVA(precoUnitarioAtual, ivaEfetivo);

    // ✅ RENDERIZA VIA PORTAL — sempre por cima de qualquer modal
    return createPortal(
        <div className="servico-modal-overlay" onClick={onClose}>
            <div className="servico-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="servico-modal-header">
                    <h3>
                        <Icon name={isEditMode ? "edit" : "add_box"} size={22} />
                        {isEditMode ? 'Editar Serviço' : 'Novo Serviço'}
                    </h3>
                    <button className="servico-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="servico-modal-body">
                    {message.text && (
                        <div className={`servico-modal-alert servico-modal-alert-${message.type}`}>
                            <Icon name={message.type === 'success' ? 'check_circle' : 'error'} size={18} />
                            <span>{message.text}</span>
                            <button className="servico-alert-close" onClick={() => setMessage({ text: '', type: '' })}>×</button>
                        </div>
                    )}

                    <form onSubmit={isEditMode ? handleEditSubmit : handleCreateSubmit}>
                        {/* TABS */}
                        <div className="servico-modal-tabs">
                            <button
                                type="button"
                                className={`servico-modal-tab-btn ${activeTab === 'geral' ? 'active' : ''}`}
                                onClick={() => setActiveTab('geral')}
                            >
                                <Icon name="info" size={16} />
                                <span className="tab-label">Dados Gerais</span>
                            </button>
                            <button
                                type="button"
                                className={`servico-modal-tab-btn ${activeTab === 'impostos' ? 'active' : ''}`}
                                onClick={() => setActiveTab('impostos')}
                            >
                                <Icon name="receipt" size={16} />
                                <span className="tab-label">Impostos</span>
                            </button>
                        </div>

                        {/* TAB DADOS GERAIS */}
                        <div className={`servico-modal-tab-panel ${activeTab === 'geral' ? 'active' : ''}`}>
                            <div className="servico-modal-section">
                                <div className="servico-form-group">
                                    <label>Nome <span className="servico-required">*</span></label>
                                    <input
                                        type="text"
                                        name="nome"
                                        value={formData.nome}
                                        onChange={handleChange}
                                        required
                                        placeholder="Nome do serviço"
                                    />
                                </div>
                                <div className="servico-form-row">
                                    <div className="servico-form-group">
                                        <label>Código</label>
                                        <input
                                            type="text"
                                            name="codigo"
                                            value={formData.codigo}
                                            onChange={handleChange}
                                            placeholder="Código (opcional)"
                                            disabled={isEditMode}
                                        />
                                        <small className="servico-form-help">Gerado automaticamente com prefixo SR</small>
                                    </div>
                                    <div className="servico-form-group">
                                        <label>Categoria</label>
                                        {/* ✅ WRAPPER para seta customizada */}
                                        <div className="servico-select-wrapper">
                                            <select name="categoriaId" value={formData.categoriaId} onChange={handleChange}>
                                                <option value="">Selecione uma categoria</option>
                                                {categorias.map(cat => (
                                                    <option key={cat._id} value={cat._id}>{cat.nome}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="servico-form-row">
                                    <div className="servico-form-group">
                                        <label>Preço Unitário <span className="servico-required">*</span></label>
                                        <input
                                            type="text"
                                            name="precoUnitario"
                                            value={formData.precoUnitario}
                                            onChange={handleChange}
                                            required
                                            placeholder="0.00"
                                            inputMode="decimal"
                                            autoComplete="off"
                                        />
                                        <small className="servico-form-help">Preço base sem IVA</small>
                                    </div>
                                    <div className="servico-form-group">
                                        <label>Preço de Venda (com IVA)</label>
                                        <input
                                            type="text"
                                            name="precoVenda"
                                            value={formData.precoVenda}
                                            onChange={handleChange}
                                            placeholder="0.00"
                                            inputMode="decimal"
                                            autoComplete="off"
                                            readOnly
                                        />
                                        {ivaEfetivo !== null && ivaEfetivo !== undefined && precoUnitarioAtual > 0 && (
                                            <small className="servico-form-help" style={{ color: 'var(--color-info)' }}>
                                                <Icon name="info" size={12} />
                                                Calculado com IVA de {ivaEfetivo}% (base: {formatarMoeda(precoUnitarioAtual)})
                                            </small>
                                        )}
                                        {ivaEfetivo === null && precoUnitarioAtual > 0 && (
                                            <small className="servico-form-help" style={{ color: 'var(--color-warning)' }}>
                                                <Icon name="warning" size={12} />
                                                Sem IVA configurado
                                            </small>
                                        )}
                                    </div>
                                </div>
                                <div className="servico-form-row">
                                    <div className="servico-form-group">
                                        <label>Tipo de Preço</label>
                                        {/* ✅ WRAPPER para seta customizada */}
                                        <div className="servico-select-wrapper">
                                            <select name="tipoPreco" value={formData.tipoPreco} onChange={handleChange}>
                                                {TIPOS_PRECO.map(t => (
                                                    <option key={t.value} value={t.value}>{t.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="servico-form-group">
                                        <label>Duração Estimada (horas)</label>
                                        <input
                                            type="number"
                                            name="duracaoEstimada"
                                            value={formData.duracaoEstimada}
                                            onChange={handleChange}
                                            placeholder="Ex: 2.5"
                                            step="0.5"
                                            min="0"
                                        />
                                        <small className="servico-form-help">Deixe em branco se não aplicável</small>
                                    </div>
                                </div>
                                <div className="servico-form-group">
                                    <label>Descrição</label>
                                    <textarea
                                        name="descricao"
                                        value={formData.descricao}
                                        onChange={handleChange}
                                        placeholder="Descrição do serviço"
                                        rows="2"
                                    />
                                </div>
                                <div className="servico-form-group">
                                    <label className="servico-checkbox-label">
                                        <input type="checkbox" name="ativo" checked={formData.ativo} onChange={handleChange} />
                                        Serviço Ativo
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* TAB IMPOSTOS */}
                        <div className={`servico-modal-tab-panel ${activeTab === 'impostos' ? 'active' : ''}`}>
                            <div className="servico-modal-section">
                                <div className="servico-form-row">
                                    <div className="servico-form-group">
                                        <label>IVA (%)</label>
                                        <input
                                            type="number"
                                            name="impostos.iva"
                                            value={formData.impostos.iva !== null && formData.impostos.iva !== undefined ? formData.impostos.iva : ''}
                                            onChange={handleChange}
                                            placeholder="Deixar em branco para usar o da empresa"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            disabled={formData.impostos.usarIvaEmpresa}
                                        />
                                        <small className="servico-form-help">
                                            {formData.impostos.usarIvaEmpresa
                                                ? `Usando IVA da empresa (${configEmpresa?.iva !== null && configEmpresa?.iva !== undefined ? `${configEmpresa.iva}%` : 'N/D'})`
                                                : formData.impostos.iva !== null ? `IVA específico: ${formData.impostos.iva}%` : 'IVA específico do serviço'}
                                        </small>
                                    </div>
                                    <div className="servico-form-group">
                                        <label>ISPC (%)</label>
                                        <input
                                            type="number"
                                            name="impostos.ispc"
                                            value={formData.impostos.ispc || 0}
                                            onChange={handleChange}
                                            placeholder="0"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                        />
                                    </div>
                                </div>
                                <div className="servico-form-group">
                                    <label className="servico-checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="impostos.usarIvaEmpresa"
                                            checked={formData.impostos.usarIvaEmpresa}
                                            onChange={handleChange}
                                        />
                                        Usar IVA da empresa ({configEmpresa?.iva !== null && configEmpresa?.iva !== undefined ? `${configEmpresa.iva}%` : 'N/D'})
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* AÇÕES */}
                        <div className="servico-modal-actions">
                            <button type="submit" className="servico-modal-btn-primary" disabled={loading}>
                                {loading ? (isEditMode ? 'A atualizar...' : 'A criar...') : (isEditMode ? 'Salvar Alterações' : 'Criar Serviço')}
                            </button>
                            <button type="button" className="servico-modal-btn-secondary" onClick={onClose}>
                                Cancelar
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body
    );
}